import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import { logEvent } from 'config/tracking';
import BACKEND_URL from 'constants/backendUrl';
import ApplicationSource from 'core/enums/applicationSource';
import { TaskPriority } from 'core/enums/taskPriority';
import { TaskSubject } from 'core/enums/taskSubject';
import Application from 'core/models/application';
import Task from 'core/models/task';
import { useInvalidateApplications } from 'core/api/applications';
import { useContacts } from 'core/api/contacts';
import { getContactLabel } from 'core/useCases/contacts/utils';
import { useModalStore } from 'stores/modal';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { startOfTomorrow } from 'date-fns';
import useResponsive from 'hooks/responsive';
import { useIsPremium } from 'hooks/userIsPremium';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SSpacedColumn } from 'styles/layout';
import { enumKeys } from 'utils/common';
import { POST, PUT } from 'utils/http';
import { TrackingEventName } from 'utils/types/amplitude';
import { DateOrTimeInput, SelectInput } from '../lib';
import ResourcesInput from '../lib/ResourcesInput';
import { OptionsProps } from '../lib/SelectInput';
import SelectMultiInput from '../lib/SelectMultiInput';
import TagsInput from '../lib/TagsInput';
import TextInput from '../lib/TextInput';

type TagInput = { id: string; name: string };

type Inputs = {
    company: string;
    job: string;
    source: ApplicationSource;
    sourceDetails?: string;
    salary?: string;
    reference?: string;
    dateOfPublication?: Date;
    place?: string;
    contactIds: string[];
    tags?: TagInput[];
    comment?: string;
};

type ApplicationFormProps = {
    closeModal(): void;
    defaultValues?: Partial<Application>;
    disabledFields?: Array<DisabledField>;
};

type CreateApplicationResponse = {
    application: Application;
    task?: Task;
};

type UpdateApplicationResponse = {
    application: Application;
};

const createApplicationRequest = async (inputs: Inputs): Promise<CreateApplicationResponse> => {
    const response = (await POST<CreateApplicationResponse>(
        `${BACKEND_URL}/api/application`,
        inputs,
    )) as CreateApplicationResponse;

    return response;
};

const updateApplicationRequest = async (inputs: Inputs, id: string): Promise<Application> => {
    const response = (await PUT<UpdateApplicationResponse>(`${BACKEND_URL}/api/application/${id}`, {
        ...inputs,
    })) as UpdateApplicationResponse;

    return response.application;
};

const computeData = (application?: Partial<Application>): Partial<Inputs> | undefined => {
    if (!application) {
        return undefined;
    }
     
    const { id, contacts, dateOfPublication, tags, ...data } = application;
    const computedData = {
        ...data,
        contactIds: contacts?.map((contact) => contact.id),
        dateOfPublication: dateOfPublication ? new Date(dateOfPublication) : undefined,
        tags: tags?.map((tag) => ({ id: tag.id, name: tag.name })) || [],
    } as Partial<Inputs>;

    return computedData;
};

const ApplicationForm: FC<ApplicationFormProps> = ({ closeModal, defaultValues }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const history = useHistory();
    const isPremium = useIsPremium();
    const openModal = useModalStore((s) => s.openModal);
    const closeModalStore = useModalStore((s) => s.closeModal);
    const invalidateApplications = useInvalidateApplications();
    const contactsData = useContacts().data;
    const contactList = useMemo(() => contactsData ?? [], [contactsData]);
    const [contactsOptions, setContactsOptions] = useState<OptionsProps>([]);
    const [sourceOptions, setSourceOptions] = useState<OptionsProps>([]);

    const handleClickToPremiumField = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const methods = useForm<Inputs>({
        defaultValues: computeData(defaultValues),
    });
    const { handleSubmit, control, watch, setValue } = methods;
    const selectedSource = watch('source');

    // TODO Handle first task after application creation
    const openFirstTaskModal = (newApplication: Application) => {
        openModal({
            defaultValues: {
                application: newApplication,
                deadline: startOfTomorrow(),
                priority: TaskPriority.Normal,
                subject: TaskSubject.SendApplication,
            },
            disabledFields: [DisabledField.ApplicationId],
            title: t('task-list.add-first-task'),
            type: ModalType.Task,
        });
    };

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            if (defaultValues?.id) {
                // Update
                await updateApplicationRequest(values, defaultValues.id);
                invalidateApplications();
                closeModalStore();
            } else {
                // Creation
                const { application } = await createApplicationRequest(values);

                invalidateApplications();
                openFirstTaskModal(application);
                logEvent({
                    name: TrackingEventName.APPLICATION_CREATED,
                    params: { applicationId: application.id, source: application.source },
                });
                closeModalStore();
            }
            toast.success(t(`application.${defaultValues?.id ? 'update' : 'creation'}.success`));
        } catch {
            toast.error(t(`application.${defaultValues?.id ? 'update' : 'creation'}.error`));
            closeModalStore();
        }
    });

    useEffect(() => {
        const sourceOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(ApplicationSource)) {
            sourceOptionsComputed.push({
                label: t(`application.source.enum.${ApplicationSource[value]}`),
                value: ApplicationSource[value],
            });
        }
        setSourceOptions(sourceOptionsComputed);
    }, [setSourceOptions]);

    useEffect(() => {
        const contacts = contactList.map((contact) => ({
            label: getContactLabel(contact, t),
            value: contact.id,
        }));
        setContactsOptions(contacts);
    }, [setContactsOptions, contactList]);

    useEffect(() => {
        if (ApplicationSource.MyNetwork === selectedSource) {
            setValue('reference', '');
        }
    }, [selectedSource, setValue]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SSpacedColumn>
                    <TextInput
                        label={t('common.company.label')}
                        name="company"
                        placeholder={t('common.company.placeholder')}
                        semi={!isMobile}
                        required
                    />
                    <TextInput
                        label={t('common.job.label')}
                        name="job"
                        placeholder={t('common.job.placeholder')}
                        semi={!isMobile}
                        required
                    />
                </SSpacedColumn>
                <SSpacedColumn>
                    <SelectInput
                        control={control}
                        label={t('application.source.label')}
                        name="source"
                        options={sourceOptions}
                        semi={!isMobile}
                        required
                    />
                    <TextInput
                        label={t('application.source-details.label')}
                        name="sourceDetails"
                        placeholder={t('application.source-details.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <SSpacedColumn>
                    <TextInput
                        label={t('application.salary.label')}
                        name="salary"
                        placeholder={t('application.salary.placeholder')}
                        semi={!isMobile}
                    />
                    {!(ApplicationSource.MyNetwork === selectedSource) && (
                        <TextInput
                            label={t('application.reference.label')}
                            name="reference"
                            placeholder={t('application.reference.placeholder')}
                            semi={!isMobile}
                        />
                    )}
                </SSpacedColumn>
                <SSpacedColumn>
                    <TextInput
                        label={t('application.place.label')}
                        name="place"
                        placeholder={t('application.place.placeholder')}
                        semi={!isMobile}
                    />
                    <DateOrTimeInput
                        control={control}
                        label={t('application.date-of-publication')}
                        name="dateOfPublication"
                        semi={!isMobile}
                    />
                </SSpacedColumn>
                <SelectMultiInput
                    control={control}
                    label={t('application.associated-contacts')}
                    name="contactIds"
                    options={contactsOptions}
                />
                <TagsInput
                    control={control as any}
                    isPremium={isPremium}
                    name="tags"
                    onClickToPremiumField={handleClickToPremiumField}
                />
                <TextInput
                    label={t('application.description.label')}
                    name="description"
                    placeholder={t('application.description.placeholder')}
                    isTextArea
                />
                <ResourcesInput name="resources" />
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{defaultValues?.id ? t('common.update') : t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default ApplicationForm;
